import React, { useState, useRef, useMemo } from 'react';
import cn from 'classnames';
import ReactSwipe from 'react-swipe';
import Dots from '@wix/da-ds/pkg/pagination/Dots';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';

import s from './SwipeSlider.scss';

const MAX_CHILDREN_COUNT = 5;

export interface Props {
  startSlide?: number;
  indicatorsClassName?: string;
  children?: React.ReactNode | undefined;
  className?: string;
  maxItems?: number;
}

const SwipeSlider: React.FC<Props> = ({
  startSlide = 0,
  indicatorsClassName,
  className,
  children,
  maxItems = MAX_CHILDREN_COUNT,
}) => {
  const [activeIndex, setActiveIndex] = useState(startSlide);
  const reactSwipeRef = useRef<ReactSwipe | null>(null);
  const items = useMemo(
    () =>
      React.Children.count(children) > maxItems
        ? React.Children.toArray(children).slice(0, maxItems)
        : React.Children.toArray(children),
    [children, maxItems]
  );

  // override swipe styles to use flexbox instead of floats which give us troubles with vertical stretching
  const swipeStyle: ReactSwipe.Style = useMemo(
    () => ({
      container: { overflow: 'hidden' },
      wrapper: {
        position: 'relative',
        display: 'flex',
        alignItems: 'stretch',
        width: `${100 * items.length}%`,
      },
      child: {
        position: 'relative',
        left: startSlide > 0 ? `-${(100.0 / items.length) * startSlide}%` : 0,
        flexBasis: `${100.0 / items.length}%`,
        flexShrink: 0,
        transitionProperty: 'transform',
      },
    }),
    [startSlide, items]
  );

  return (
    <div className={cn(s['root'], className)}>
      <ErrorBoundary debugComponent="SwipeSlider">
        <ReactSwipe
          ref={swipeRef => (reactSwipeRef.current = swipeRef) as any}
          childCount={items.length}
          style={swipeStyle}
          swipeOptions={{
            startSlide,
            continuous: false,
            callback: index => {
              // this call fixes some jerkyness possibly caused by re-render before swipe ends
              window.requestAnimationFrame(() => {
                setActiveIndex(index);
              });
            },
          }}
        >
          {items.map((child, index) => (
            <div key={child ? child['key'] : index}>{child}</div>
          ))}
        </ReactSwipe>
        {items.length > 1 && (
          <div className={cn(s['indicators'], indicatorsClassName)}>
            <Dots
              total={items.length}
              current={activeIndex}
              onClick={index => {
                setActiveIndex(index);
                reactSwipeRef.current && reactSwipeRef.current.slide(index, 0);
              }}
            />
          </div>
        )}
      </ErrorBoundary>
    </div>
  );
};
SwipeSlider.displayName = 'SwipeSlider';

export default SwipeSlider;
